import { useEffect, useState } from 'react';
import { api, HealthResponse, ReadyResponse } from '../lib/api';
import {
  Home,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  Server,
  Database,
  HardDrive,
} from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  status?: 'success' | 'warning' | 'error';
}

function StatCard({ title, value, icon, status }: StatCardProps) {
  const statusColors = {
    success: 'bg-green-50 border-green-200 text-green-700',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    error: 'bg-red-50 border-red-200 text-red-700',
  };

  return (
    <div
      className={`rounded-lg border p-6 ${
        status ? statusColors[status] : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-1 text-2xl font-semibold">{value}</p>
        </div>
        <div className="text-gray-400">{icon}</div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [ready, setReady] = useState<ReadyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);

    const [healthRes, readyRes] = await Promise.all([
      api.healthz(),
      api.readyz(),
    ]);

    if (healthRes.error) {
      setError(healthRes.error.message);
    } else {
      setHealth(healthRes.data!);
    }

    if (readyRes.data) {
      setReady(readyRes.data);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500">
            Welcome to your PowerCore product demo
          </p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-mazo-600 px-4 py-2 text-white hover:bg-mazo-700 disabled:opacity-50"
        >
          <Activity className="h-4 w-4" />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <XCircle className="h-5 w-5" />
            <span className="font-medium">Connection Error</span>
          </div>
          <p className="mt-1 text-sm text-red-600">{error}</p>
          <p className="mt-2 text-xs text-red-500">
            Make sure the backend is running at{' '}
            {import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}
          </p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Service Status"
          value={health?.status === 'ok' ? 'Healthy' : 'Unknown'}
          icon={<Server className="h-6 w-6" />}
          status={health?.status === 'ok' ? 'success' : undefined}
        />
        <StatCard
          title="Service Name"
          value={health?.service || 'N/A'}
          icon={<Home className="h-6 w-6" />}
        />
        <StatCard
          title="Version"
          value={health?.version || 'N/A'}
          icon={<Activity className="h-6 w-6" />}
        />
        <StatCard
          title="Last Check"
          value={
            health?.timestamp
              ? new Date(health.timestamp).toLocaleTimeString()
              : 'N/A'
          }
          icon={<Clock className="h-6 w-6" />}
        />
      </div>

      {/* Readiness Checks */}
      {ready && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Readiness Checks
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Database className="h-5 w-5 text-gray-400" />
                <span className="font-medium">Database</span>
              </div>
              <div className="flex items-center gap-2">
                {ready.checks.database === 'ok' ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-green-600">Connected</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-500" />
                    <span className="text-red-600">Error</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <HardDrive className="h-5 w-5 text-gray-400" />
                <span className="font-medium">Storage (R2)</span>
              </div>
              <div className="flex items-center gap-2">
                {ready.checks.storage === 'ok' ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-green-600">Connected</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-500" />
                    <span className="text-red-600">Error</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Info */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Configuration
          </h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">API Base URL</dt>
              <dd className="font-mono text-gray-900">
                {import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Logto Endpoint</dt>
              <dd className="font-mono text-gray-900 truncate max-w-[200px]">
                {import.meta.env.VITE_LOGTO_ENDPOINT || 'Not configured'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Links
          </h2>
          <div className="space-y-2">
            <a
              href="https://github.com/powercore"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-mazo-600 hover:underline"
            >
              PowerCore Documentation
            </a>
            <a
              href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/healthz`}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-mazo-600 hover:underline"
            >
              Backend Health Endpoint
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
